import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { CaretLeft, CaretRight, Warning, Pencil } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import EditReportModal from "@/components/EditReportModal";

const MONTHS_SV = [
  "januari", "februari", "mars", "april", "maj", "juni",
  "juli", "augusti", "september", "oktober", "november", "december",
];

function ymd(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function fmtSeconds(sec) {
  const h = Math.floor((sec || 0) / 3600);
  const m = Math.floor(((sec || 0) % 3600) / 60);
  return `${h}h ${String(m).padStart(2, "0")}m`;
}

function fmtTimeRange(start, end) {
  const opts = { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Stockholm" };
  const s = new Date(start).toLocaleTimeString("sv-SE", opts);
  const e = end ? new Date(end).toLocaleTimeString("sv-SE", opts) : "…";
  return `${s} – ${e}`;
}

/** YYYY-MM-DD i Europe/Stockholm — så nattpass mellan 22:00–02:00 UTC
 *  hamnar rätt dag i den svenska kalendern. */
function localDayKey(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("sv-SE", {
      timeZone: "Europe/Stockholm",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  } catch {
    return String(iso).slice(0, 10);
  }
}

export default function MyMonth() {
  const { user } = useAuth();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth()); // 0-indexed
  const [reports, setReports] = useState([]);
  const [objects, setObjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const period = useMemo(() => {
    const start = new Date(year, month, 1);
    const end = new Date(year, month + 1, 0);
    return { start_from: ymd(start), start_to: ymd(end) };
  }, [year, month]);

  const load = async () => {
    try {
      setLoading(true);
      const [r, o, t] = await Promise.all([
        api.get(`/reports?start_from=${period.start_from}&start_to=${period.start_to}`),
        api.get("/objects"),
        api.get("/tasks"),
      ]);
      setReports(r.data || []);
      setObjects(o.data || []);
      setTasks(t.data || []);
    } catch (e) {
      toast.error("Kunde inte hämta månadsdata");
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
     
  }, [period.start_from, period.start_to]);

  const totalSec = reports
    .filter((r) => r.status === "completed")
    .reduce((s, r) => s + (r.duration_seconds || 0), 0);

  // Gruppa per dag (YYYY-MM-DD, Europe/Stockholm)
  const byDay = useMemo(() => {
    const m = new Map();
    for (const r of reports) {
      const day = localDayKey(r.start_time);
      if (!m.has(day)) m.set(day, []);
      m.get(day).push(r);
    }
    return Array.from(m.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [reports]);

  const deviationCount = reports.filter((r) => (r.deviation || "").trim()).length;

  const prev = () => {
    if (month === 0) {
      setMonth(11);
      setYear(year - 1);
    } else {
      setMonth(month - 1);
    }
  };
  const next = () => {
    if (month === 11) {
      setMonth(0);
      setYear(year + 1);
    } else {
      setMonth(month + 1);
    }
  };
  const today = () => {
    const n = new Date();
    setYear(n.getFullYear());
    setMonth(n.getMonth());
  };

  return (
    <div className="space-y-6" data-testid="my-month-page">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-4xl md:text-5xl tracking-tight">Mina timmar</h1>
          <p className="text-text-muted mt-1">
            Månadsöversikt för <strong>{user?.name}</strong>. Klicka på en rapport för att
            flagga en avvikelse innan attest.
          </p>
        </div>
      </header>

      {/* Månadsväljare */}
      <section className="tk-card p-4 flex items-center gap-3 flex-wrap justify-between" data-testid="month-selector">
        <div className="flex items-center gap-2">
          <button
            data-testid="month-prev"
            onClick={prev}
            className="tk-btn-outline !p-2"
            aria-label="Föregående månad"
          >
            <CaretLeft size={18} weight="bold" />
          </button>
          <div className="min-w-[180px] text-center">
            <div className="font-heading text-2xl capitalize">
              {MONTHS_SV[month]} {year}
            </div>
            <div className="text-xs text-text-muted">
              {period.start_from} → {period.start_to}
            </div>
          </div>
          <button
            data-testid="month-next"
            onClick={next}
            className="tk-btn-outline !p-2"
            aria-label="Nästa månad"
          >
            <CaretRight size={18} weight="bold" />
          </button>
          <button data-testid="month-today" onClick={today} className="tk-btn-outline text-xs !py-2 ml-2">
            Idag
          </button>
        </div>

        <div className="flex items-center gap-4">
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-text-muted">Totalt</div>
            <div className="font-heading text-2xl tabular-nums" data-testid="month-total">
              {fmtSeconds(totalSec)}
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-text-muted">Avvikelser</div>
            <div
              data-testid="month-deviation-count"
              className={`font-heading text-2xl tabular-nums ${deviationCount > 0 ? "text-[var(--brand-rust)]" : ""}`}
            >
              {deviationCount}
            </div>
          </div>
        </div>
      </section>

      {/* Rapportlista per dag */}
      {loading ? (
        <div className="tk-card p-6 text-sm text-text-muted">Laddar…</div>
      ) : reports.length === 0 ? (
        <div className="tk-card p-8 text-center text-sm text-text-muted" data-testid="month-empty">
          Inga rapporter registrerade denna månad.
        </div>
      ) : (
        <div className="space-y-4">
          {byDay.map(([day, rows]) => {
            const daySec = rows
              .filter((r) => r.status === "completed")
              .reduce((s, r) => s + (r.duration_seconds || 0), 0);
            return (
              <section key={day} className="tk-card overflow-hidden" data-testid={`day-${day}`}>
                <header className="bg-[var(--brand-sand)]/40 px-5 py-2.5 flex items-center justify-between border-b border-app">
                  <div className="font-heading text-lg">{day}</div>
                  <div className="text-sm text-text-muted tabular-nums">
                    {fmtSeconds(daySec)}
                  </div>
                </header>
                <div className="divide-y divide-app">
                  {rows.map((r) => (
                    <div
                      key={r.report_id}
                      data-testid={`report-row-${r.report_id}`}
                      className="px-5 py-3 flex items-center justify-between gap-3 flex-wrap hover:bg-[var(--brand-sand)]/20"
                    >
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">
                          {r.object_name}
                          <span className="text-text-muted"> · </span>
                          <span className="text-text-muted">{r.task_name}</span>
                        </div>
                        <div className="text-xs text-text-muted tabular-nums">
                          {fmtTimeRange(r.start_time, r.end_time)}
                          {r.status === "active" && (
                            <span className="ml-2 tk-pill bg-[var(--brand-forest)]/10 text-[var(--brand-forest)] text-[10px]">
                              Pågår
                            </span>
                          )}
                        </div>
                        {(r.deviation || "").trim() && (
                          <div
                            data-testid={`report-deviation-${r.report_id}`}
                            className="mt-1 text-xs text-[var(--brand-rust)] flex items-center gap-1"
                          >
                            <Warning size={13} weight="fill" />
                            <span className="line-clamp-2">{r.deviation}</span>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="tabular-nums text-sm text-text-muted">
                          {fmtSeconds(r.duration_seconds || 0)}
                        </span>
                        <button
                          data-testid={`report-flag-${r.report_id}`}
                          onClick={() => setEditing(r)}
                          className="tk-btn-outline !py-1.5 !px-3 text-xs inline-flex items-center gap-1.5 border-[var(--brand-rust)]/40 text-[var(--brand-rust)] hover:bg-[var(--brand-rust)]/10"
                          title="Flagga avvikelse på detta moment"
                        >
                          {(r.deviation || "").trim() ? (
                            <>
                              <Pencil size={13} /> Ändra
                            </>
                          ) : (
                            <>
                              <Warning size={13} /> Flagga
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      )}

      {editing && (
        <EditReportModal
          report={editing}
          objects={objects}
          tasks={tasks}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await load();
          }}
        />
      )}
    </div>
  );
}
